import type { DocNode } from '@atlaskit/adf-schema';
import type { ADFEntity } from '@atlaskit/adf-utils/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import type { ConfluenceCreatePageCommandProperties } from '@atlassian/rovo-content-bridge-api-commands/commands/confluence-create-page-command';

import type { DocumentCreateCommandProcessor } from '../stream-processors/create-command-processor';

import type { PassThroughCommandProps } from './command-handler';
import { StreamingCommandHandler } from './streaming-command-handler';

/**
 * Recursively walks an ADF entity and collects the file IDs of all media nodes.
 */
interface MediaNodeInfo {
	collection?: string;
	id: string;
}

function collectMediaNodes(node: ADFEntity | undefined, nodes: Map<string, MediaNodeInfo>): void {
	if (!node) {
		return;
	}
	if (node.type === 'media' && typeof node.attrs?.id === 'string') {
		nodes.set(node.attrs.id as string, {
			id: node.attrs.id as string,
			collection: node.attrs.collection as string | undefined,
		});
	}
	if (Array.isArray(node.content)) {
		for (const child of node.content) {
			collectMediaNodes(child, nodes);
		}
	}
}

export class CreateCommandHandler extends StreamingCommandHandler<ConfluenceCreatePageCommandProperties> {
	/**
	 * Tracks media file IDs we've already reported via onTrackAIGeneratingMedia
	 * during this streaming session, so we only call it once per file ID.
	 */
	private trackedMediaIds: Set<string> = new Set();

	constructor(
		props: PassThroughCommandProps,
		private createCommandProcessor: DocumentCreateCommandProcessor,
	) {
		super(props, 'create');
	}

	/**
	 * Walks the streaming parser's current partial document for media nodes and
	 * calls onTrackAIGeneratingMedia for any newly-seen file IDs.
	 */
	private trackNewMediaNodes(): void {
		if (!expValEquals('aifc_page_create_with_rovo_include_infographics', 'isEnabled', true)) {
			return;
		}
		const partialDoc = this.createCommandProcessor.getPartialDocument() as ADFEntity | undefined;
		if (!partialDoc) {
			return;
		}
		const currentNodes = new Map<string, MediaNodeInfo>();
		collectMediaNodes(partialDoc, currentNodes);

		for (const [id, info] of currentNodes) {
			if (this.trackedMediaIds.has(id)) {
				continue;
			}
			this.trackedMediaIds.add(id);
			this.props.onTrackAIGeneratingMedia?.(id, info.collection);
		}
	}

	process(payload: ConfluenceCreatePageCommandProperties): void {
		if (!payload.data.adf) {
			return;
		}

		// Record (shape only) the receiver lifecycle facts at command arrival,
		// before any replay/init mutates processor state. No-op unless the debug
		// gate is enabled; the processor stores only the first arrival per stream.
		const previousAccumulatedData = payload.data.previousAccumulatedData;
		this.createCommandProcessor.markCommandArrived({
			processorWasInitializedWhenCommandArrived: this.createCommandProcessor.isInitialized(),
			previousAccumulatedDataPresent: !!previousAccumulatedData,
			previousAccumulatedDataStartsWithDocEnvelope: previousAccumulatedData
				? previousAccumulatedData.trimStart().startsWith('{"type":"doc"')
				: undefined,
		});

		if (!this.createCommandProcessor.isInitialized() && payload.data.previousAccumulatedData) {
			// The scenario where the command processor is not initialized but the payload contains
			// accumulated ADF can happen if the user closes and reopens the modal during streaming.
			// To handle this, we first clear the document (in case it first streamed for long enough
			// to trigger a draft sync) and then stream in the accumulated ADF so that streaming
			// subsequent chunks can continue correctly.
			this.props.editorAPI.core.actions.replaceDocument('', {
				addToHistory: false,
				scrollIntoView: false,
			});
			// Record (shape only) that this stream is a replay of previously
			// accumulated data, so blank-page debug telemetry can attribute
			// failures to the modal close/reopen mid-stream path.
			this.createCommandProcessor.markPreviousAccumulatedDataReplay(
				payload.data.previousAccumulatedData,
			);
			this.createCommandProcessor.stream(
				payload.data.previousAccumulatedData,
				false,
				typeof payload.traceId === 'string' ? payload.traceId : undefined,
				payload.singleInstrumentationId,
			);
			this.trackNewMediaNodes();
		}

		if (!payload.isStreamComplete) {
			this.createCommandProcessor.stream(
				payload.data.adf,
				this.isStreamFinished,
				typeof payload.traceId === 'string' ? payload.traceId : undefined,
				payload.singleInstrumentationId,
			);
			this.trackNewMediaNodes();
		}
	}

	async finishAction(): Promise<DocNode | undefined> {
		const adfDoc = await this.createCommandProcessor.finish({
			onError: this.props.onStreamError,
			onRepair: this.props.onStreamRepair,
		});

		if (adfDoc) {
			this.props.editorAPI.core.actions.replaceDocument(adfDoc, {
				addToHistory: false,
				scrollIntoView: false,
			});
			// Ensure all expands are open
			this.props.editorAPI.core.actions.execute(
				this.props.editorAPI.expand?.commands.toggleExpandRange(),
			);
		}

		return adfDoc;
	}

	abort(_reason?: 'aborted' | 'timeout', _options?: { suppressError?: boolean }): void {
		this.createCommandProcessor.abort();
	}
}
